import React, { useEffect, useMemo, useState } from 'react';
import { YMaps, Map, Placemark, Polygon, Clusterer, ZoomControl } from 'react-yandex-maps';
import { Table, Spin, Alert, Statistic } from 'antd';
import { VideoCameraOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import BasicLayout from '../../components/layout/BasicLayout';
import { AkmolaCoords } from '../../constants';
import { fetchCameraMapList } from '../../services/cameramap';
import { getErrorStatus } from '../../utils/getNetwork';
import styles from './CameraMap.module.scss';

const AKMOLA_CENTER = [51.7, 70.5];

const firstDefined = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] !== undefined && values[i] !== null) return values[i];
  }
  return undefined;
};

const pickCoords = (item) => {
  if (Array.isArray(item.coordinates) && item.coordinates.length === 2) {
    return item.coordinates.map(Number);
  }
  if (Array.isArray(item.coords) && item.coords.length === 2) {
    return item.coords.map(Number);
  }
  if (Array.isArray(item.position) && item.position.length === 2) {
    return item.position.map(Number);
  }
  const lat = firstDefined(item.lat, item.latitude, item.y);
  const lng = firstDefined(item.lng, item.lon, item.long, item.longitude, item.x);
  if (lat != null && lng != null) {
    const parsed = [Number(lat), Number(lng)];
    if (parsed.every((n) => Number.isFinite(n))) return parsed;
  }
  return null;
};

const pickName = (item, index) => (
  item.name ||
  item.title ||
  item.label ||
  item.cameraName ||
  (item.id != null ? 'Камера #' + item.id : 'Камера ' + (index + 1))
);

const normalize = (raw) =>
  raw
    .map((item, index) => {
      const coords = pickCoords(item);
      if (!coords) return null;
      const key = item.id != null
        ? String(item.id)
        : coords[0] + ',' + coords[1] + ',' + index;
      return {
        key,
        name: pickName(item, index),
        coords,
        address: item.address || item.location || item.place || '',
      };
    })
    .filter(Boolean);

const formatCoord = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(6) : value
);

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.content)) return payload.content;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
};

const extractError = (err, fallback) => {
  if (err && err.data && err.data.message) return err.data.message;
  if (err && err.message) return err.message;
  return fallback;
};

const CameraMap = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchCameraMapList()
      .then((res) => {
        if (cancelled) return;
        setCameras(normalize(extractList(res && res.data)));
      })
      .catch((err) => {
        if (cancelled) return;
        const message = getErrorStatus(err) === 403
          ? t('error.doNotHaveAccessToViewThisModule')
          : extractError(err, t('cameraMap.loadError'));
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const columns = useMemo(
    () => [
      {
        title: '№',
        key: 'index',
        width: 60,
        render: (_value, _row, index) => index + 1,
      },
      {
        title: t('cameraMap.name'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t('cameraMap.address'),
        dataIndex: 'address',
        key: 'address',
        render: (value) => value || '—',
      },
      {
        title: t('cameraMap.latitude'),
        key: 'lat',
        width: 140,
        render: (_value, record) => formatCoord(record.coords[0]),
      },
      {
        title: t('cameraMap.longitude'),
        key: 'lng',
        width: 140,
        render: (_value, record) => formatCoord(record.coords[1]),
      },
    ],
    [t],
  );

  return (
    <BasicLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h3>
            <VideoCameraOutlined />
            {t('cameraMap.title')}
          </h3>
          <Statistic
            title={t('cameraMap.totalLabel')}
            value={cameras.length}
            prefix={<VideoCameraOutlined />}
          />
        </div>

        {error && <Alert type="error" message={error} showIcon />}

        <Spin spinning={loading} tip={t('roadRepairPlan.loadingPleaseWait') + '...'}>
          <div className={styles.mapWrapper}>
            <YMaps query={{ lang: 'ru_RU' }}>
              <Map
                defaultState={{ center: AKMOLA_CENTER, zoom: 7, controls: [] }}
                width="100%"
                height="500px"
                modules={[
                  'control.ZoomControl',
                  'geoObject.addon.balloon',
                  'geoObject.addon.hint',
                ]}
              >
                <ZoomControl options={{ float: 'right' }} />
                <Polygon
                  geometry={[AkmolaCoords]}
                  options={{
                    fillColor: 'rgba(24, 144, 255, 0.08)',
                    strokeColor: '#1890ff',
                    strokeWidth: 2,
                    opacity: 1,
                    interactivityModel: 'default#transparent',
                  }}
                />
                <Clusterer
                  options={{
                    preset: 'islands#redClusterIcons',
                    groupByCoordinates: false,
                    clusterDisableClickZoom: false,
                  }}
                >
                  {cameras.map((cam) => (
                    <Placemark
                      key={cam.key}
                      geometry={cam.coords}
                      properties={{
                        hintContent: cam.name,
                        balloonContentHeader: cam.name,
                        balloonContentBody: cam.address || '',
                        balloonContentFooter:
                          formatCoord(cam.coords[0]) + ', ' + formatCoord(cam.coords[1]),
                      }}
                      options={{ preset: 'islands#redIcon' }}
                    />
                  ))}
                </Clusterer>
              </Map>
            </YMaps>
          </div>

          <Table
            rowKey="key"
            columns={columns}
            dataSource={cameras}
            size="small"
            scroll={{ x: 600 }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        </Spin>
      </div>
    </BasicLayout>
  );
};

export default CameraMap;
